// Avatar: uploaded JPEG via CloudFront, else Google picture, else colored initials (D-7).

import { useState } from "react";
import { AVATAR_COLORS, isAvatarColor } from "@checklist/shared";

const INITIAL_COLORS = AVATAR_COLORS;

function hashScreenName(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) >>> 0;
  }
  return h;
}

function firstChar(s: string): string {
  return Array.from(s)[0] ?? "";
}

export function initialsFromScreenName(screenName: string): string {
  const parts = screenName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return Array.from(parts[0]!).slice(0, 2).join("").toUpperCase();
  return (firstChar(parts[0]!) + firstChar(parts[parts.length - 1]!)).toUpperCase();
}

/**
 * First-initial + last-initial when BOTH name parts are known; otherwise fall
 * back to the first two characters of the screen name (D-7).
 */
export function initialsFor(opts: {
  firstName?: string;
  lastName?: string;
  screenName: string;
}): string {
  const first = opts.firstName?.trim();
  const last = opts.lastName?.trim();
  if (first && last) return (firstChar(first) + firstChar(last)).toUpperCase();
  return initialsFromScreenName(opts.screenName || "?");
}

export function colorFromScreenName(screenName: string): string {
  return INITIAL_COLORS[hashScreenName(screenName) % INITIAL_COLORS.length]!;
}

export function siteOrigin(): string {
  const fromEnv = (import.meta.env.VITE_SITE_URL as string | undefined)?.replace(/\/+$/, "");
  if (fromEnv) return fromEnv;
  if (typeof window !== "undefined") return window.location.origin;
  return "";
}

/** CloudFront avatar URL with cache-bust query (PLAN.md Task 1.3). */
export function cloudFrontAvatarUrl(sub: string, updatedAt?: string): string {
  const base = siteOrigin();
  const v = updatedAt ? `?v=${encodeURIComponent(updatedAt)}` : "";
  return `${base}/avatars/${sub}.jpg${v}`;
}

export interface AvatarProps {
  sub: string;
  screenName: string;
  firstName?: string;
  lastName?: string;
  /** User-chosen background for the colored-initials fallback; one of AVATAR_COLORS. */
  color?: string;
  avatarKey?: string;
  googlePicture?: string;
  updatedAt?: string;
  size?: number;
  className?: string;
}

export function Avatar({
  sub,
  screenName,
  firstName,
  lastName,
  color,
  avatarKey,
  googlePicture,
  updatedAt,
  size = 40,
  className = "",
}: AvatarProps) {
  const [imgFailed, setImgFailed] = useState(false);

  const uploadedSrc = avatarKey ? cloudFrontAvatarUrl(sub, updatedAt) : null;
  const googleSrc = !avatarKey && googlePicture ? googlePicture : null;
  const src = !imgFailed ? uploadedSrc ?? googleSrc : null;

  const initials = initialsFor({ firstName, lastName, screenName: screenName || "?" });
  const bg = isAvatarColor(color) ? color : colorFromScreenName(screenName || sub);

  if (src) {
    return (
      <img
        src={src}
        alt=""
        width={size}
        height={size}
        className={`shrink-0 rounded-full object-cover ${className}`}
        style={{ width: size, height: size }}
        onError={() => setImgFailed(true)}
        referrerPolicy="no-referrer"
      />
    );
  }

  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white ${className}`}
      style={{
        width: size,
        height: size,
        backgroundColor: bg,
        fontSize: Math.max(10, Math.round(size * 0.38)),
      }}
      aria-hidden
    >
      {initials}
    </span>
  );
}
